import React, { useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useModalBehavior } from './useModalBehavior';
import { getModalAnimations, ModalAnimationType } from './ModalAnimations';
import {
  getModalSizeClasses,
  getModalVariantClasses,
  combineModalClasses,
  ModalSize,
  ModalVariant,
} from './ModalVariants';

/**
 * Base Modal Component
 * Core modal functionality with animations, variants, and behaviors
 */

// =============================================================================
// Base Modal Props Interface
// =============================================================================

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;

  // Appearance
  title?: string;
  size?: ModalSize;
  variant?: ModalVariant;
  animation?: ModalAnimationType;

  // Behavior
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  lockBodyScroll?: boolean;
  manageFocus?: boolean;

  // Layout
  header?: React.ReactNode;
  footer?: React.ReactNode;

  // Styling
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  backdropClassName?: string;

  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  role?: string;
}

// =============================================================================
// Base Modal Component
// =============================================================================

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,

  // Appearance
  title,
  size = 'md',
  variant = 'default',
  animation = 'default',

  // Behavior
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  lockBodyScroll = true,
  manageFocus = true,

  // Layout
  header,
  footer,

  // Styling
  className = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  backdropClassName = '',

  // Accessibility
  ariaLabel,
  ariaDescribedBy,
  role = 'dialog',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Get configurations
  const sizeConfig = getModalSizeClasses(size);
  const variantConfig = getModalVariantClasses(variant);
  const animationConfig = getModalAnimations(animation);

  // Use modal behaviors
  const { handleBackdropClick } = useModalBehavior({
    isOpen,
    onClose,
    closeOnEscape,
    closeOnBackdrop,
    lockBodyScroll,
    manageFocus,
    modalRef,
  });

  // Determine if header should be rendered
  const shouldRenderHeader = title || header || showCloseButton;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={animationConfig.backdrop}
          initial="initial"
          animate="animate"
          exit="exit"
          className={combineModalClasses(
            `fixed inset-0 z-50 flex items-center justify-center ${sizeConfig.padding} ${variantConfig.backdrop}`,
            backdropClassName
          )}
          onClick={handleBackdropClick}
          role="presentation"
        >
          <motion.div
            ref={modalRef}
            variants={animationConfig.content}
            initial="initial"
            animate="animate"
            exit="exit"
            className={combineModalClasses(
              `
                relative ${sizeConfig.width} ${sizeConfig.maxWidth} 
                ${sizeConfig.height || 'max-h-[90vh]'} 
                ${variantConfig.modal} rounded-2xl overflow-hidden
                ${variantConfig.focus}
              `,
              className
            )}
            onClick={e => e.stopPropagation()}
            role={role}
            aria-modal="true"
            aria-label={ariaLabel || title}
            aria-describedby={ariaDescribedBy}
            tabIndex={-1}
          >
            {/* Header */}
            {shouldRenderHeader && (
              <div
                className={combineModalClasses(
                  `
                  flex items-center justify-between px-6 py-4 
                  border-b ${variantConfig.header}
                  ${size === 'full' ? 'rounded-t-2xl' : ''}
                `,
                  headerClassName
                )}
              >
                <div className="flex-1">
                  {title && (
                    <h2 className={`text-xl font-semibold ${variantConfig.title}`}>{title}</h2>
                  )}
                  {header && !title && header}
                </div>

                {showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    className={`
                      p-2 rounded-lg transition-colors 
                      ${variantConfig.closeButton}
                      ml-4 flex-shrink-0
                    `}
                    aria-label="Modalı kapat"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Body */}
            <div
              className={combineModalClasses(
                `
                overflow-y-auto flex-1 
                ${shouldRenderHeader ? 'px-6 py-4' : 'p-6'}
                ${footer ? 'pb-0' : ''}
              `,
                bodyClassName
              )}
            >
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div
                className={combineModalClasses(
                  `
                  px-6 py-4 border-t ${variantConfig.border}
                  bg-gray-50 rounded-b-2xl 
                `,
                  footerClassName
                )}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// Modal Header Component
// =============================================================================

interface ModalHeaderProps {
  children: React.ReactNode;
  variant?: ModalVariant;
  className?: string;
  onClose?: () => void;
  showCloseButton?: boolean;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  variant = 'default',
  className = '',
  onClose,
  showCloseButton = true,
}) => {
  const variantConfig = getModalVariantClasses(variant);

  return (
    <div
      className={combineModalClasses(
        `flex items-center justify-between px-6 py-4 border-b ${variantConfig.header}`,
        className
      )}
    >
      <div className="flex-1">{children}</div>

      {showCloseButton && onClose && (
        <button
          type="button"
          onClick={onClose}
          className={`
            p-2 rounded-lg transition-colors 
            ${variantConfig.closeButton}
            ml-4 flex-shrink-0
          `}
          aria-label="Modalı kapat"
        >
          <FaTimes className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// =============================================================================
// Modal Body Component
// =============================================================================

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  scrollable?: boolean;
}

export const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  className = '',
  scrollable = true,
}) => {
  return (
    <div
      className={combineModalClasses(
        `
        flex-1 px-6 py-4
        ${scrollable ? 'overflow-y-auto' : ''}
      `,
        className
      )}
    >
      {children}
    </div>
  );
};

// =============================================================================
// Modal Footer Component
// =============================================================================

interface ModalFooterProps {
  children: React.ReactNode;
  variant?: ModalVariant;
  className?: string;
  align?: 'left' | 'center' | 'right' | 'between';
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  variant = 'default',
  className = '',
  align = 'right',
}) => {
  const variantConfig = getModalVariantClasses(variant);

  const getAlignmentClass = () => {
    switch (align) {
      case 'left':
        return 'justify-start';
      case 'center':
        return 'justify-center';
      case 'between':
        return 'justify-between';
      case 'right':
      default:
        return 'justify-end';
    }
  };

  return (
    <div
      className={combineModalClasses(
        `
        px-6 py-4 border-t ${variantConfig.border}
        bg-gray-50 rounded-b-2xl flex gap-3 ${getAlignmentClass()}
      `,
        className
      )}
    >
      {children}
    </div>
  );
};
